import { useEffect, useState } from "react";

/**
 * Tiny hash-based router. Reads `#tab/arg` from the URL so deep links work
 * (`#brand/DUNIYA` lands on the Brand drilldown for DUNIYA) without pulling
 * react-router in for two routes.
 *
 * Returns `[tab, arg, navigate]`. `navigate("brand", "DUNIYA")` updates the
 * hash and re-renders.
 */
export const useHashRoute = (defaultTab = "overview"): [
  string,
  string | null,
  (tab: string, arg?: string) => void,
] => {
  const parse = (raw: string): [string, string | null] => {
    const clean = raw.replace(/^#\/?/, "").trim();
    if (!clean) return [defaultTab, null];
    const [tab, ...rest] = clean.split("/");
    return [tab || defaultTab, rest.join("/") || null];
  };

  const [state, setState] = useState<[string, string | null]>(() =>
    parse(window.location.hash),
  );

  useEffect(() => {
    const onHashChange = () => setState(parse(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = (tab: string, arg?: string) => {
    const next = arg ? `#${tab}/${arg}` : `#${tab}`;
    if (window.location.hash !== next) {
      window.location.hash = next;
    } else {
      // Force re-render even if the hash didn't change (e.g. clicking the
      // same tab) by toggling state directly.
      setState([tab, arg ?? null]);
    }
  };

  return [state[0], state[1], navigate];
};
